import { logError } from '@zg-rentals/log-error';

// Logger is optional - apps can pass their own logger if needed
interface Logger {
  info: (message: string) => void;
}

let logger: Logger | undefined;

/**
 * Initialize the retry utilities with an optional logger.
 * This should be called once during app initialization if logging is desired.
 *
 * @param appLogger - The logger instance from the app's bootstrap
 *
 * @example
 * ```typescript
 * // In app bootstrap
 * import { initRetryLogger } from '@zg-rentals/satellite-components';
 * import { bootstrap } from './bootstrap';
 *
 * initRetryLogger(bootstrap.logger);
 * ```
 */
export const initRetryLogger = (appLogger: Logger): void => {
  logger = appLogger;
};

/**
 * Executes an async operation with fast retry logic optimized for user experience.
 * Uses progressive delays (200ms, 300ms, 400ms, 500ms) to provide quick feedback
 * while still allowing transient network issues to resolve.
 *
 * @param operation - The async function to execute and retry on failure
 * @param maxAttempts - Maximum number of attempts (default: 5)
 * @param operationName - Human-readable name for logging purposes
 * @returns Promise that resolves with the operation result or rejects after all retries fail
 *
 * @example
 * ```typescript
 * const result = await retryWithFastBackoff(
 *   () => apiCall(),
 *   5,
 *   'API token generation'
 * );
 * ```
 */
export const retryWithFastBackoff = async <T>(
  operation: () => Promise<T>,
  maxAttempts = 5,
  operationName: string,
  attemptNumber = 1,
): Promise<T> => {
  try {
    return await operation();
  } catch (err) {
    logError({
      error: err,
      context: {
        attemptNumber,
        maxAttempts,
        operationName,
        info: `Error in ${operationName}`,
      },
    });

    // Retry if we haven't reached max attempts
    if (attemptNumber < maxAttempts) {
      // Fast progressive delays optimized for UX: 200ms, 300ms, 400ms, 500ms
      // Total time to failure: ~1.5 seconds - fast enough for immediate user feedback
      const retryDelay = API_RETRY_CONFIG.BASE_DELAY + attemptNumber * API_RETRY_CONFIG.DELAY_INCREMENT;

      if (logger) {
        logger.info(`Retrying ${operationName} in ${retryDelay}ms... (${attemptNumber + 1}/${maxAttempts})`);
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
      return retryWithFastBackoff(operation, maxAttempts, operationName, attemptNumber + 1);
    }

    // Final failure - re-throw error for caller to handle
    throw err;
  }
};

/**
 * Common retry configuration for API calls in the satellite app.
 * Provides consistent retry behavior across different API operations.
 */
export const API_RETRY_CONFIG = {
  /** Standard number of retry attempts for API calls */
  MAX_ATTEMPTS: 5,

  /** Base delay for retry calculations (200ms) */
  BASE_DELAY: 200,

  /** Delay increment per attempt (100ms) */
  DELAY_INCREMENT: 100,
} as const;

/**
 * Convenience wrapper for API calls with standard retry configuration.
 * Uses the common API retry settings defined in API_RETRY_CONFIG.
 *
 * @param operation - The API operation to retry
 * @param operationName - Human-readable name for logging
 * @returns Promise that resolves with the operation result
 *
 * @example
 * ```typescript
 * const token = await retryApiCall(
 *   () => fetchUploadToken(),
 *   'upload token generation'
 * );
 * ```
 */
export const retryApiCall = async <T>(operation: () => Promise<T>, operationName: string): Promise<T> => {
  return retryWithFastBackoff(operation, API_RETRY_CONFIG.MAX_ATTEMPTS, operationName);
};
